import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import {
  NoProviderService,
  NoProviderServiceProduct,
} from '../gantt/interfaces/NoProviderService.model';
import { Provider } from 'src/app/models/provider/provider.model';
import {
  AddProviderRequest,
  AddProviderResponse,
  ProviderServiceDetail,
} from 'src/app/models/service-detail/service-detail.model';
import { HistoryServicesService } from 'src/app/services/history-services/history-services.service';
import { takeUntil } from 'rxjs';
import { TuiAlertService } from '@taiga-ui/core';
import { TranslateService } from '@ngx-translate/core';

enum Step {
  Step1 = 1,
  Step2 = 2,
  Step3 = 3,
}

@Component({
  selector: 'app-services-without-provider-side',
  templateUrl: './services-without-provider-side.component.html',
  styleUrls: ['./services-without-provider-side.component.css'],
})
export class ServicesWithoutProviderSideComponent {
  @Input() NoProviderServices: NoProviderService[] = [];
  @Input() ZoneId: string = '';
  @Output() noProviderServicesChange = new EventEmitter<NoProviderService[]>();

  FilterdNoProviderServices: NoProviderService[] = [];
  ServiceSelected: NoProviderService | undefined;
  ProductSelected: NoProviderServiceProduct | undefined;
  ServiceSearchText: string = '';

  ProviderSelected: ProviderServiceDetail | undefined;
  SaveLoading = false;

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  Step = Step;
  currentStep: Step = Step.Step1;

  constructor(
    private offcanvasService: NgbOffcanvas,
    private HistoryServicesService: HistoryServicesService,
    private alerts: TuiAlertService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    if (this.NoProviderServices) {
      this.FilterdNoProviderServices = [...this.NoProviderServices];
    }
  }

  closeCanvas(): void {
    this.offcanvasService.dismiss();
  }

  normalizeText(str: string): string {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  filterServices(text: string): void {
    if (text == '') {
      this.FilterdNoProviderServices = [...this.NoProviderServices];
    } else {
      let textToFilter = this.normalizeText(text).toLocaleLowerCase();
      this.FilterdNoProviderServices = [...this.NoProviderServices].filter(
        (service: NoProviderService) => {
          return (
            this.normalizeText(service.clientName ?? '')
              .toLowerCase()
              .includes(textToFilter) ||
            this.normalizeText(service.licensePlate)
              .toLowerCase()
              .includes(textToFilter) ||
            this.normalizeText(
              this.translate.instant(
                'PRODUCT_NAMES.' + service.serviceProducts[0].productName
              )
            )
              .toLowerCase()
              .includes(textToFilter) ||
            this.normalizeText(service.originLocation.address)
              .toLowerCase()
              .includes(textToFilter) ||
            this.normalizeText(service.destinationLocation.address)
              .toLowerCase()
              .includes(textToFilter)
          );
        }
      );
    }
  }

  focusInput(): void {
    this.searchInput.nativeElement.focus();
  }

  selectService(orderHash: string, service: NoProviderService): void {
    this.ServiceSelected = service;
    if (this.moreThanOneProduct(service)) {
      this.setStep2();
    } else {
      this.ProductSelected = service.serviceProducts[0];
      this.setStep3();
    }
  }

  setStep1(): void {
    this.currentStep = Step.Step1;
  }

  setStep2(): void {
    this.currentStep = Step.Step2;
    this.setProductsListHeight();
  }

  setStep3(): void {
    this.currentStep = Step.Step3;
    this.setProviderListHeight();
  }

  ProviderListHeight = '';
  setProviderListHeight(): void {
    setTimeout(() => {
      const source = document.getElementById('source-div');
      const height = source?.offsetHeight;
      if (height) {
        this.ProviderListHeight = `calc(100vh - ${337 + height}px)`;
      } else {
        this.ProviderListHeight = `calc(100vh - 517px)`;
      }
    });
  }

  ProductsListHeight = '';
  setProductsListHeight(): void {
    setTimeout(() => {
      const source = document.getElementById('service-card');
      const height = source?.offsetHeight;
      if (height) {
        this.ProductsListHeight = `calc(100vh - ${257 + height}px)`;
      } else {
        this.ProductsListHeight = `calc(100vh - 534px)`;
      }
    });
  }

  moreThanOneProduct(service: NoProviderService): boolean {
    return service.serviceProducts?.length > 1;
  }

  selectProductNext(product: NoProviderServiceProduct): void {
    this.selectProduct(product);
    this.setStep3();
  }

  selectProduct(product: NoProviderServiceProduct): void {
    this.ProductSelected = product;
  }

  goBack(): void {
    if (this.currentStep == Step.Step2) {
      this.currentStep--;
    }
    if (this.currentStep == Step.Step3) {
      if (this.moreThanOneProduct(this.ServiceSelected!)) {
        this.currentStep--;
      } else {
        this.currentStep = Step.Step1;
      }
    }
  }

  assignProvider(): void {
    if (
      !this.ServiceSelected ||
      !this.ProviderSelected ||
      !this.ProductSelected
    )
      return;

    this.SaveLoading = true;
    const payload: AddProviderRequest = {
      orderHash: this.ServiceSelected.orderHash,
      productId: this.ProductSelected.productGuid,
      providerId: this.ProviderSelected.id,
    };
    // this.SaveLoading = false;

    // const ServiceCopy = this.ServiceSelected;
    // const ProductCopy = this.ProductSelected;
    // this.returnToAssign(payload);
    // this.showSuccesAlert(ServiceCopy, ProductCopy);

    this.HistoryServicesService.addProviderToProductInService(
      payload
    ).subscribe({
      next: (res: AddProviderResponse) => {
        if (res.wasSuccessful) {
          this.SaveLoading = false;
          const ServiceCopy = this.ServiceSelected;
          const ProductCopy = this.ProductSelected;
          this.returnToAssign(payload);
          this.showSuccesAlert(ServiceCopy, ProductCopy);

          if (this.NoProviderServices.length == 0) {
            this.closeCanvas();
          }
        } else {
          this.SaveLoading = false;
          this.showErrorAlert();
        }
      },
      error: (err) => {
        this.SaveLoading = false;
        this.showErrorAlert();
      },
    });
  }

  showSuccesAlert(
    ServiceCopy: NoProviderService | undefined,
    product: NoProviderServiceProduct | undefined
  ): void {
    if (this.ServiceSelected) {
      this.showSuccessAlertProduct(product);
    } else {
      this.showSuccessAlertService(ServiceCopy);
    }
  }

  showSuccessAlertProduct(product: NoProviderServiceProduct | undefined): void {
    this.alerts
      .open(
        `${this.translate.instant(
          'NO_PROVIDER_SERVICE.ALERTS.0'
        )} ${this.translate.instant(
          'PRODUCT_NAMES.' + product?.productName
        )} ${this.translate.instant('NO_PROVIDER_SERVICE.ALERTS.1')}`,
        {
          label: `${this.translate.instant('NO_PROVIDER_SERVICE.ALERTS.2')}`,
          status: 'success',
          autoClose: 3000,
          hasCloseButton: false,
        }
      )
      .subscribe();
  }

  showSuccessAlertService(Service: NoProviderService | undefined): void {
    this.alerts
      .open(
        `${this.translate.instant(
          'NO_PROVIDER_SERVICE.ALERTS.3'
        )} ${this.translate.instant(
          'PRODUCT_NAMES.' + Service?.serviceProducts[0].productName
        )} - ${Service?.orderHash} ${this.translate.instant(
          'NO_PROVIDER_SERVICE.ALERTS.4'
        )}`,
        {
          label: `${this.translate.instant('NO_PROVIDER_SERVICE.ALERTS.5')}`,
          status: 'success',
          autoClose: 3000,
          hasCloseButton: false,
        }
      )
      .subscribe();
  }

  showErrorAlert(): void {
    this.alerts
      .open(`${this.translate.instant('NO_PROVIDER_SERVICE.ALERTS.6')}`, {
        label: `${this.translate.instant('NO_PROVIDER_SERVICE.ALERTS.7')}`,
        status: 'error',
        autoClose: 4000,
        hasCloseButton: false,
      })
      .subscribe();
  }

  returnToAssign(payload: AddProviderRequest): void {
    this.ProductSelected = undefined;
    this.ProviderSelected = undefined;
    this.cleanProductAssigned(payload);
    this.ServiceSelected ? this.setStep2() : this.setStep1();
    this.noProviderServicesChange.emit(this.NoProviderServices);
  }

  cleanProductAssigned(payload: AddProviderRequest): void {
    this.ServiceSelected = this.cleanProductAssignedOfService(
      this.ServiceSelected!,
      payload.productId
    );

    if (this.checkAllProductsAssigned(this.ServiceSelected)) {
      this.removeService(payload.orderHash);
    } else {
      this.cleanProductAssignedOfServiceArray(
        payload.productId,
        payload.orderHash
      );
    }
  }

  cleanProductAssignedOfService(
    service: NoProviderService,
    productId: string
  ): NoProviderService {
    service.serviceProducts.map((product: NoProviderServiceProduct) => {
      if (product.productGuid == productId) {
        product.isProviderAssigned = true;
      }
      return product;
    });
    return service;
  }

  cleanProductAssignedOfServiceArray(
    productId: string,
    orderHash: string
  ): void {
    this.NoProviderServices = this.NoProviderServices.map(
      (parent: NoProviderService) => {
        if (parent.orderHash === orderHash) {
          return this.cleanProductAssignedOfService(parent, productId);
        }
        return parent;
      }
    );
  }

  checkAllProductsAssigned(service: NoProviderService): boolean {
    return service.serviceProducts
      .filter(
        (product: NoProviderServiceProduct) => product.doesRequiresProvider
      )
      .every((product: NoProviderServiceProduct) => product.isProviderAssigned);
  }

  removeService(orderHash: string): void {
    this.ServiceSelected = undefined;
    this.NoProviderServices = this.NoProviderServices.filter(
      (service: NoProviderService) => {
        return service.orderHash !== orderHash;
      }
    );
    this.filterServices('');
  }
}
